import { match } from '@/utils/pattern-match-util'
import { type InventoryItemPricing } from '@/features/inventory/InventoryItem'
import type { JobType } from '@/features/jobs/job-type'

/**
 * What to narrow an inventory item search to.
 */
export interface InventoryItemSearchFilter {
  priceTypes?: InventoryItemPricing[]
  archivedStatus?: InventoryItemArchivedFilterOptions
  proVisibilityStatus?: InventoryItemProVisibilityFilterOptions
  overrideStatus?: InventoryItemOverrideFilterOptions
  jobType?: JobType
}

/**
 * Builds the inventory item search API's filter query.
 *
 * @param filter
 */
export function buildInventoryItemSearchFilter(
  filter: InventoryItemSearchFilter,
) {
  return new InventoryItemRequestQueryFilterBuilder()
    .withPriceTypes(filter.priceTypes)
    .withArchivedStatus(filter.archivedStatus)
    .withProVisibilityStatus(filter.proVisibilityStatus)
    .withOverrideStatus(filter.overrideStatus)
    .forJobType(filter.jobType)
    .build()
}

/**
 * Query string builder for the InventoryItem Search API.
 */
class InventoryItemRequestQueryFilterBuilder {
  priceTypes = ''
  itemTypes = ''
  archivedStatus?: InventoryItemArchivedFilterOptions = undefined
  proVisibilityStatus?: InventoryItemProVisibilityFilterOptions = undefined
  overrideStatus?: InventoryItemOverrideFilterOptions = undefined

  /**
   * Filter by price type.
   *
   * @param priceType
   */
  withPriceTypes(
    priceType?: InventoryItemPricing[],
  ): InventoryItemRequestQueryFilterBuilder {
    if (!priceType) {
      return this
    }

    this.priceTypes += priceType
      .map((s) => condition('price_type', '=', toPriceType(s)))
      .join(' OR ')
    return this
  }

  /**
   * Filters by the item types the given job type allows.
   *
   * @param jobType
   */
  forJobType(jobType?: JobType): InventoryItemRequestQueryFilterBuilder {
    if (!jobType) {
      return this
    }

    switch (jobType.type) {
      case 'Tax': {
        this.itemTypes =
          'item_type=CONSULTATION OR item_type=TAX OR item_type=EXTENSION OR item_type=DEFAULT'
        break
      }
      case 'Consultation': {
        this.itemTypes = 'item_type=CONSULTATION OR item_type=DEFAULT'
        break
      }
      case 'Bookkeeping': {
        this.itemTypes = 'item_type=BOOKKEEPING OR item_type=DEFAULT'
        break
      }
      case 'Misc': {
        this.itemTypes = 'item_type=DEFAULT OR item_type=EXTENSION'
        break
      }
      case 'Unspecified': {
        break
      }
    }

    return this
  }

  /**
   * Filter by archived status.
   *
   * @param archivedStatus
   */
  withArchivedStatus(
    archivedStatus?: InventoryItemArchivedFilterOptions,
  ): InventoryItemRequestQueryFilterBuilder {
    if (archivedStatus === undefined) {
      return this
    }
    this.archivedStatus = archivedStatus
    return this
  }

  /**
   * Filter by pro visibility status.
   *
   * @param proVisibilityStatus
   */
  withProVisibilityStatus(
    proVisibilityStatus?: InventoryItemProVisibilityFilterOptions,
  ): InventoryItemRequestQueryFilterBuilder {
    if (proVisibilityStatus === undefined) {
      return this
    }
    this.proVisibilityStatus = proVisibilityStatus
    return this
  }

  /**
   * Filter by override status.
   *
   * @param overrideStatus
   */
  withOverrideStatus(
    overrideStatus?: InventoryItemOverrideFilterOptions,
  ): InventoryItemRequestQueryFilterBuilder {
    if (overrideStatus === undefined) {
      return this
    }
    this.overrideStatus = overrideStatus
    return this
  }

  /**
   * Builds the query string.
   */
  build(): string {
    const queryParts = []

    if (this.priceTypes) {
      queryParts.push(`(${this.priceTypes})`)
    }

    if (this.archivedStatus !== undefined) {
      queryParts.push(`archived=${toArchivedValue(this.archivedStatus)}`)
    }

    if (
      this.proVisibilityStatus !== undefined &&
      this.proVisibilityStatus !== 'All'
    ) {
      queryParts.push(
        `pro_visibility=${toProVisibilityValue(this.proVisibilityStatus)}`,
      )
    }

    if (
      this.proVisibilityStatus !== undefined &&
      this.proVisibilityStatus === 'All'
    ) {
      queryParts.push(
        `pro_visibility=${toProVisibilityValue('Visible')} OR pro_visibility=${toProVisibilityValue('Hidden')}`,
      )
    }

    if (this.overrideStatus !== undefined) {
      queryParts.push(`overrides=${toOverrideValue(this.overrideStatus)}`)
    }

    if (this.itemTypes) {
      queryParts.push(`(${this.itemTypes})`)
    }

    return queryParts.join(' AND ')
  }
}

/**
 * Generates a condition and escapes the passed value.
 *
 * @param field
 * @param operator
 * @param value
 */
function condition(
  field: string,
  operator: ConditionOperator,
  value: string,
): string {
  // eslint-disable-next-line no-useless-escape
  return `${field}${operator}\"${value}\"`
}

type ConditionOperator = '='

function toPriceType(status: InventoryItemPricing) {
  return match(status, {
    Fee: () => 'FEE',
    Flat: () => 'FLAT',
    Markup: () => 'MARKUP',
    Markdown: () => 'MARKDOWN',
  })
}

function toArchivedValue(status: InventoryItemArchivedFilterOptions) {
  return match(status, {
    All: () => 'ALL',
    Archived: () => 'ARCHIVED',
    Active: () => 'ACTIVE',
  })
}

function toOverrideValue(status: InventoryItemOverrideFilterOptions) {
  return match(status, {
    All: () => 'ALL',
    Only: () => 'ONLY',
    Exclude: () => 'EXCLUDE',
  })
}

function toProVisibilityValue(status: 'Hidden' | 'Visible') {
  return match(status, {
    Hidden: () => 0,
    Visible: () => 1,
  })
}

export type InventoryItemArchivedFilterOptions = 'All' | 'Archived' | 'Active'

export type InventoryItemOverrideFilterOptions = 'All' | 'Only' | 'Exclude'

export type InventoryItemProVisibilityFilterOptions =
  'Hidden' | 'Visible' | 'All'
